import { useMemo } from 'react';
import {
  ComposedChart,
  Line,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { clsx } from 'clsx';

interface HistoryDataPoint {
  date: string;
  sentiment_score?: number;
  disconnect_score?: number;
  price?: number;
  signal?: string;
}

interface HistoricalChartProps {
  data: HistoryDataPoint[];
  className?: string;
  showSentiment?: boolean;
  showDisconnect?: boolean;
  showPrice?: boolean;
}

const formatDate = (dateStr: string) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const formatPrice = (value: number) => {
  return `$${value.toFixed(2)}`;
};

const formatScore = (value: number) => {
  return value.toFixed(0);
};

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{
    name: string;
    value: number;
    color: string;
    dataKey: string;
  }>;
  label?: string;
}

const CustomTooltip = ({ active, payload, label }: CustomTooltipProps) => {
  if (!active || !payload || !payload.length) return null;

  return (
    <div className="bg-[var(--color-bg-primary)] border border-[var(--color-border)] rounded-lg p-3 shadow-lg">
      <p className="text-sm font-medium text-[var(--color-text-primary)] mb-2">
        {label ? formatDate(label) : ''}
      </p>
      <div className="space-y-1">
        {payload.map((entry, index) => (
          <div key={index} className="flex items-center gap-2 text-sm">
            <div
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-[var(--color-text-muted)]">{entry.name}:</span>
            <span className="font-medium text-[var(--color-text-primary)]">
              {entry.dataKey === 'price'
                ? formatPrice(entry.value)
                : formatScore(entry.value)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export function HistoricalChart({
  data,
  className,
  showSentiment = true,
  showDisconnect = true,
  showPrice = true,
}: HistoricalChartProps) {
  // Process data to ensure we have valid numbers
  const chartData = useMemo(() => {
    return data.map((point) => ({
      ...point,
      sentiment_score: point.sentiment_score ?? undefined,
      disconnect_score: point.disconnect_score ?? undefined,
      price: point.price ?? undefined,
    }));
  }, [data]);

  // Calculate price domain for secondary Y-axis
  const priceDomain = useMemo(() => {
    const prices = chartData.filter((d) => d.price != null).map((d) => d.price!);
    if (prices.length === 0) return [0, 100];
    const min = Math.min(...prices);
    const max = Math.max(...prices);
    const padding = (max - min) * 0.1;
    return [Math.floor(min - padding), Math.ceil(max + padding)];
  }, [chartData]);

  if (!data || data.length === 0) {
    return (
      <div
        className={clsx(
          'flex items-center justify-center h-64 rounded-lg',
          'bg-[var(--color-bg-secondary)] text-[var(--color-text-muted)]',
          className
        )}
      >
        No historical data available
      </div>
    );
  }

  return (
    <div className={clsx('w-full h-80', className)}>
      <ResponsiveContainer width="100%" height="100%" minHeight={1}>
        <ComposedChart
          data={chartData}
          margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            vertical={false}
            stroke="var(--color-border)"
          />

          <XAxis
            dataKey="date"
            tickFormatter={formatDate}
            tick={{ fill: 'var(--color-text-muted)', fontSize: 12 }}
            axisLine={{ stroke: 'var(--color-border)' }}
            tickLine={false}
            interval="preserveStartEnd"
          />

          {/* Left Y-axis for scores (-100 to +100) */}
          <YAxis
            yAxisId="scores"
            domain={[-100, 100]}
            tick={{ fill: 'var(--color-text-muted)', fontSize: 12 }}
            axisLine={{ stroke: 'var(--color-border)' }}
            tickLine={false}
            tickFormatter={formatScore}
          />

          {/* Right Y-axis for price */}
          {showPrice && (
            <YAxis
              yAxisId="price"
              orientation="right"
              domain={priceDomain}
              tick={{ fill: 'var(--color-text-muted)', fontSize: 12 }}
              axisLine={{ stroke: 'var(--color-border)' }}
              tickLine={false}
              tickFormatter={formatPrice}
            />
          )}

          <Tooltip content={<CustomTooltip />} />

          <Legend
            wrapperStyle={{ paddingTop: 10, fontSize: '11px' }}
            formatter={(value) => (
              <span className="text-[var(--color-text-secondary)] text-xs">{value}</span>
            )}
          />

          {/* Sentiment score as line */}
          {showSentiment && (
            <Line
              yAxisId="scores"
              type="monotone"
              dataKey="sentiment_score"
              name="Sentiment"
              stroke="#a855f7"
              strokeWidth={2}
              dot={{ r: 2, fill: '#a855f7' }}
              activeDot={{ r: 4 }}
            />
          )}

          {/* Disconnect score as line */}
          {showDisconnect && (
            <Line
              yAxisId="scores"
              type="monotone"
              dataKey="disconnect_score"
              name="Disconnect"
              stroke="#3b82f6"
              strokeWidth={2}
              dot={{ r: 2, fill: '#3b82f6' }}
              activeDot={{ r: 4 }}
            />
          )}

          {/* Price as line on secondary axis */}
          {showPrice && (
            <Line
              yAxisId="price"
              type="monotone"
              dataKey="price"
              name="Price"
              stroke="#10b981"
              strokeWidth={2}
              dot={{ r: 2, fill: '#10b981' }}
              activeDot={{ r: 4 }}
            />
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

// Mini chart variant for dashboards
export function MiniHistoricalChart({
  data,
  dataKey = 'disconnect_score',
  color = '#3b82f6',
  className,
}: {
  data: HistoryDataPoint[];
  dataKey?: 'disconnect_score' | 'sentiment_score' | 'price';
  color?: string;
  className?: string;
}) {
  const chartData = useMemo(() => {
    return data.filter((d) => d[dataKey] != null);
  }, [data, dataKey]);

  if (chartData.length < 2) return null;

  return (
    <div className={clsx('w-full h-16', className)}>
      <ResponsiveContainer width="100%" height="100%" minHeight={1}>
        <ComposedChart data={chartData}>
          <defs>
            <linearGradient id={`miniGradient-${color}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={color} stopOpacity={0.3} />
              <stop offset="95%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <Area
            type="monotone"
            dataKey={dataKey}
            stroke={color}
            fill={`url(#miniGradient-${color})`}
            strokeWidth={1.5}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
